import { useMemo } from "react";
import { Card } from "@/core/components/ui/card";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  IcmAnimatedMoney,
  IcmMoneyDisplay,
  splitFormattedMoney,
  IcmTabbedPieChart,
} from "@/icm/common";
import type { EnrichedIcmStatement, IcmJob } from "@/icm/types";
import { buildJobsPieChartTabs } from "@/icm/utils";
import { getSummaryMetrics } from "./utils";

interface IcmPageSummaryCardProps {
  statements: EnrichedIcmStatement[];
  jobs: IcmJob[];
}

export const IcmPageSummaryCard = ({
  statements,
  jobs,
}: IcmPageSummaryCardProps) => {
  const {
    totalPayout,
    totalRevenue,
    totalRevenueFormatted,
    commissionRate,
    uniqueJobCount,
  } = useMemo(() => getSummaryMetrics(statements, jobs), [statements, jobs]);
  const pieChartTabs = useMemo(() => buildJobsPieChartTabs(jobs), [jobs]);
  const revenue = splitFormattedMoney(totalRevenueFormatted);
  const hasJobs = jobs.length > 0;

  return (
    <Card className="w-full p-8 [@media(max-width:1275px)]:w-fit">
      <Stack
        direction="row"
        justify="between"
        align="center"
        fullWidth
        className="[@media(max-width:1275px)]:flex-col [@media(max-width:1275px)]:items-start"
      >
        <Stack direction="row" align="center" gap="xl" className="flex-wrap">
          <Stack gap="xs" className="px-6">
            <Typography variant="subtitleW3" textColor="secondary">
              Total Pay
            </Typography>
            <IcmAnimatedMoney
              value={totalPayout}
              className="text-4xl font-bold"
              centsClassName="text-base font-normal text-muted-foreground"
            />
          </Stack>
          <Separator orientation="vertical" className="h-20" />

          <Stack>
            <Stack gap="xs">
              <Typography variant="subtitleW3" textColor="secondary">
                Technicians
              </Typography>
              <Typography variant="titleW2" textColor="tertiary">
                {statements.length}
              </Typography>
            </Stack>
            {uniqueJobCount > 0 && (
              <Stack gap="xs">
                <Typography variant="subtitleW3" textColor="secondary">
                  Jobs Worked
                </Typography>
                <Typography variant="titleW2" textColor="tertiary">
                  {uniqueJobCount}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Separator orientation="vertical" className="h-20" />

          <Stack>
            {totalRevenue > 0 && (
              <Stack gap="xs">
                <Typography variant="subtitleW3" textColor="secondary">
                  Total Revenue
                </Typography>
                <IcmMoneyDisplay
                  dollars={revenue.dollars}
                  cents={revenue.cents}
                  className="text-3xl font-bold text-tertiary-text"
                  centsClassName="pt-[2px] text-sm font-normal text-muted-foreground"
                />
              </Stack>
            )}

            {commissionRate !== null && (
              <Stack gap="xs">
                <Typography variant="subtitleW3" textColor="secondary">
                  Pay % of Revenue
                </Typography>
                <Typography variant="titleW2" textColor="tertiary">
                  {commissionRate.toFixed(1)}%
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        {hasJobs && (
          <Stack className="px-6">
            <IcmTabbedPieChart tabs={pieChartTabs} />
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
